import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SidebarProvider } from "@/components/ui/sidebar";
import AdminSidebar from "./Sidebar";
import Header from "./Header";
import { 
  Calculator, 
  Calendar as CalendarIcon, 
  School, 
  Info,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  TrendingUp,
  CreditCard,
  GraduationCap,
  History
} from "lucide-react";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  onSnapshot
} from "firebase/firestore";
import { db, auth } from "@/firebase/config";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { getCurrentTerm, Term, getApplicableTermFees, normalizeDate, calculateFeeBalances } from "@/utils/feeCalculations";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

// --- Types ---

interface SimulationEvent {
  id: string;
  date: string; // ISO Date
  type: 'ENROLLMENT' | 'PAYMENT' | 'TERM_SWITCH' | 'CLASS_UPGRADE' | 'GRADUATION';
  description: string;
  amount: number; // For payments (positive) or fees (negative/debit if we tracked strict ledger, but here tracked separately)
  balanceAfter: number;
}

interface VirtualStudent {
  name: string;
  classUid: string;
  className: string;
  term: number; // 1, 2, 3
  year: number; // 1 = 1st Year of simulation
  balance: number; // Current Outstanding Balance
  history: SimulationEvent[];
  
  // Track current year's fee context
  currentYearFees: {
    term1: number;
    term2: number;
    term3: number;
  };
  totalPaid: number;
}

const TestCaseSimulation = () => {
  const { toast } = useToast();
  
  // --- Auth & Context ---
  const [schoolID, setSchoolID] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  // --- Data State ---
  const [schoolClasses, setSchoolClasses] = useState<{ name: string; classUid: string; nextClassUid?: string }[]>([]);
  const [levelFees, setLevelFees] = useState<any[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  const [schoolType, setSchoolType] = useState<string>("");
  const [loadingData, setLoadingData] = useState(false);

  // --- Simulation Input State ---
  const [selectedClass, setSelectedClass] = useState("");
  const [studentType, setStudentType] = useState<string>("");
  const [admissionDate, setAdmissionDate] = useState<string>(new Date().toISOString().split('T')[0]);
  
  const [simulationResult, setSimulationResult] = useState<any>(null); // For the preview
  const [detectedTerm, setDetectedTerm] = useState<Term | null>(null);

  // --- Active Simulation State ---
  const [virtualStudent, setVirtualStudent] = useState<VirtualStudent | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>("");

  // --- Ongoing Student State ---
  const [isOngoing, setIsOngoing] = useState(false);
  const [initialDebt, setInitialDebt] = useState<string>("");
  const [balanceType, setBalanceType] = useState<"debit" | "credit">("debit");

  // --- Auth & Permissions ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAuthLoading(false);
        return;
      }

      try {
        const token = await getIdTokenResult(user);
        const claims: any = token.claims;
        const affiliations = claims.affiliations || [];
        const defaultAff = claims.defaultAffiliation;
        const claimSchoolID = claims.schoolID;

        const adminAff = defaultAff?.role === "admin" && defaultAff.schoolID
          ? defaultAff
          : affiliations.find((aff: any) => aff.role === "admin" && aff.schoolID);

        if (adminAff) {
          setSchoolID(adminAff.schoolID);
        } else if (claimSchoolID) {
            setSchoolID(claimSchoolID);
        }
      } catch (error) {
        console.error("Auth error:", error);
      } finally {
        setIsAuthLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // --- Data Fetching ---
  useEffect(() => {
    if (!schoolID) return;

    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [schoolDocSnap, feeDocSnap, termsSnap] = await Promise.all([
          getDocs(query(collection(db, `schools/${schoolID}/schoolDetails`), where("__name__", "==", "main"))),
          getDoc(doc(db, `schools/${schoolID}/deployedFeeStructures/current`)),
          getDocs(collection(db, `schools/${schoolID}/terms`))
        ]);

        if (!schoolDocSnap.empty) {
          const data = schoolDocSnap.docs[0].data();
          setSchoolType(data.type || "");
        }

        if (feeDocSnap.exists()) {
          setLevelFees(feeDocSnap.data().levelFees || []);
        }

        const termsList = termsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Term));
        setTerms(termsList);

      } catch (error) {
        console.error("Error fetching simulation data:", error);
        toast({ title: "Data Error", description: "Failed to load school configuration data.", variant: "destructive" });
      } finally {
        setLoadingData(false);
      }
    };

    const fetchClasses = () => {
        const classesRef = collection(db, `schools/${schoolID}/classes`);
        return onSnapshot(classesRef, (snapshot) => {
            const classData = snapshot.docs.map(doc => ({
                name: doc.data().name as string,
                classUid: doc.id,
                nextClassUid: doc.data().nextClassUid as string | undefined,
            })).sort((a, b) => a.name.localeCompare(b.name));
            setSchoolClasses(classData);
        });
    };

    fetchData();
    const unsubscribeClasses = fetchClasses();
    return () => unsubscribeClasses();

  }, [schoolID, toast]);


  // --- Helper: Find Fee Structure for a Class ---
  const getFeesForClass = (className: string) => {
      const matchingLevel = levelFees.find((level: any) => 
        level.classes && level.classes.some((c: string) => 
            c.toLowerCase() === className.toLowerCase() ||
            className.toLowerCase().includes(c.toLowerCase())
        )
    );

    if (!matchingLevel) return { term1: 0, term2: 0, term3: 0, annual: 0 };

    const categories = matchingLevel.categories || [];
    const isDay = studentType === "Day School"; // Note: uses current studentType state

    let t1 = 0, t2 = 0, t3 = 0;
    categories.forEach((cat: any) => {
        const v1 = isDay ? (cat.daySchoolTerm1 ?? cat.term1 ?? 0) : (cat.term1 ?? 0);
        const v2 = isDay ? (cat.daySchoolTerm2 ?? cat.term2 ?? 0) : (cat.term2 ?? 0);
        const v3 = isDay ? (cat.daySchoolTerm3 ?? cat.term3 ?? 0) : (cat.term3 ?? 0);
        t1 += Number(v1) || 0;
        t2 += Number(v2) || 0;
        t3 += Number(v3) || 0;
    });

    return { term1: t1, term2: t2, term3: t3, annual: t1 + t2 + t3 };
  };

  // --- Effect: Preview Logic (Pre-Enrollment) ---
  useEffect(() => {
    if (!selectedClass || !schoolID || virtualStudent) { // Don't run preview if active simulation
        if (!virtualStudent) {
            setSimulationResult(null);
            setDetectedTerm(null);
        }
        return;
    }

    const simDate = new Date(admissionDate);
    const termContext = getCurrentTerm(terms, simDate);
    setDetectedTerm(termContext);

    const feeStruct = getFeesForClass(selectedClass);
    // Calc applicable based on date
    const applicable = getApplicableTermFees(admissionDate, feeStruct, terms);

    setSimulationResult({
        potential: feeStruct,
        applicable: applicable
    });

  }, [selectedClass, admissionDate, studentType, levelFees, terms, virtualStudent]);


  // --- Formatters ---
  const formatMoney = (amount: number) => 
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount);

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString();


  // --- Actions ---

  const handleEnroll = () => {
    if (!simulationResult || !selectedClass) return;

    // Determine initial Term Number (1, 2, or 3)
    let initialTerm = 1;
    if (detectedTerm) initialTerm = detectedTerm.number;
    else {
        // Fallback logic if date is weird
        const m = new Date(admissionDate).getMonth();
        if (m > 8) initialTerm = 3; 
        else if (m > 3) initialTerm = 2;
    }

    // NEW LOGIC: Only apply the fee for the SPECIFIC starting term
    // Unless it's an ongoing student, then we might want to just start with debt + current term
    // For simplicity, even for ongoing, we apply the current term's fee as "New Term Fee"
    
    let initialBalance = 0;
    let historyLog: SimulationEvent[] = [];

    // 1. Handle Initial Debt (if ongoing)
    if (isOngoing && initialDebt) {
        let debt = parseFloat(initialDebt) || 0;
        
        // If Credit, treat as negative debt (surplus)
        if (balanceType === "credit") {
            debt = -debt;
        }

        if (debt !== 0) {
            initialBalance += debt;
            historyLog.push({
                id: `init-debt-${Date.now()}`,
                date: new Date().toISOString().split('T')[0],
                type: 'TERM_SWITCH', 
                description: balanceType === "credit" ? "Opening Credit (Overpayment)" : "Opening Balance Brought Forward",
                amount: debt,
                balanceAfter: initialBalance
            });
        }
    }

    const termKey = `term${initialTerm}` as keyof typeof simulationResult.applicable;
    const initialFee = simulationResult.applicable[termKey] || 0;
    
    // Add current term fee
    initialBalance += initialFee;

    const selectedClassObj = schoolClasses.find(c => c.name === selectedClass);

    historyLog.push({
        id: Date.now().toString(),
        date: new Date().toISOString().split('T')[0],
        type: 'ENROLLMENT',
        description: `Enrolled in ${selectedClass} (Term ${initialTerm})`,
        amount: initialFee,
        balanceAfter: initialBalance
    });

    const newStudent: VirtualStudent = {
        name: "Simulated Student",
        classUid: selectedClassObj?.classUid || "unknown",
        className: selectedClass,
        term: initialTerm,
        year: 1,
        balance: initialBalance,

        history: historyLog,
        currentYearFees: simulationResult.applicable,
        totalPaid: 0
    };

    setVirtualStudent(newStudent);
    toast({ title: "Simulation Started", description: `Student enrolled. Term ${initialTerm} fees applied.` });
  };

  const handlePayment = () => {
    if (!virtualStudent || !paymentAmount) return;
    const amount = Number(paymentAmount);
    if (isNaN(amount) || amount <= 0) return;

    const newBalance = virtualStudent.balance - amount;
    const newPaid = virtualStudent.totalPaid + amount;

    const event: SimulationEvent = {
        id: Date.now().toString(),
        date: new Date().toISOString().split('T')[0], // Simulate "today"
        type: 'PAYMENT',
        description: 'Fee Payment',
        amount: amount,
        balanceAfter: newBalance
    };

    setVirtualStudent({
        ...virtualStudent,
        balance: newBalance,
        totalPaid: newPaid,
        history: [event, ...virtualStudent.history]
    });
    setPaymentAmount("");
    toast({ title: "Payment Recorded", className: "bg-green-100 border-green-200 text-green-900" });
  };

  const handleNextTerm = () => {
    if (!virtualStudent) return;

    // Logic: 
    // If Term 1 -> Term 2. 
    // If Term 2 -> Term 3. 
    // If Term 3 -> Term 1 (Next Class).

    let nextTerm = virtualStudent.term + 1;
    let nextClass = virtualStudent.className;
    let nextClassUid = virtualStudent.classUid;
    let nextYear = virtualStudent.year;
    let feeAddition = 0;
    let eventType: SimulationEvent['type'] = 'TERM_SWITCH';
    let description = `Proceeded to Term ${nextTerm}`;
    let newYearFees = virtualStudent.currentYearFees;

    if (nextTerm > 3) {
        // NEW YEAR / NEW CLASS
        nextTerm = 1;
        nextYear += 1;
        eventType = 'CLASS_UPGRADE';
        
        // 1. Try explicit promotion path via nextClassUid
        const currentClassObj = schoolClasses.find(c => c.classUid === virtualStudent.classUid);
        const targetNextUid = currentClassObj?.nextClassUid;

        if (targetNextUid && targetNextUid !== 'ALUMNI') {
            const nextClassObj = schoolClasses.find(c => c.classUid === targetNextUid);
            if (nextClassObj) {
                nextClass = nextClassObj.name;
                nextClassUid = nextClassObj.classUid;
                description = `Promoted to ${nextClass} (Year ${nextYear})`;
                
                // Calculate fees for new class
                const fees = getFeesForClass(nextClass);
                newYearFees = { term1: fees.term1, term2: fees.term2, term3: fees.term3 };
                feeAddition = fees.term1; 
            } else {
                 eventType = 'GRADUATION';
                 description = "Graduated (End of Classes)";
                 feeAddition = 0;
            }
        } else if (targetNextUid === 'ALUMNI') {
             eventType = 'GRADUATION';
             description = "Graduated (End of Classes)";
             feeAddition = 0;
        } else {
            // 2. Fallback: Index-based promotion
            const currentIdx = schoolClasses.findIndex(c => c.name === virtualStudent.className);
            if (currentIdx !== -1 && currentIdx < schoolClasses.length - 1) {
                const nextClassData = schoolClasses[currentIdx + 1];
                nextClass = nextClassData.name;
                nextClassUid = nextClassData.classUid;
                description = `Promoted to ${nextClass} (Year ${nextYear})`;
                
                const fees = getFeesForClass(nextClass);
                newYearFees = { term1: fees.term1, term2: fees.term2, term3: fees.term3 };
                feeAddition = fees.term1;
            } else {
                eventType = 'GRADUATION';
                description = "Graduated (End of Classes)";
                feeAddition = 0;
            }
        }
    } else {
        // Just moving terms within same year
        // Add fee for next term
        const termKey = `term${nextTerm}` as keyof typeof virtualStudent.currentYearFees;
        feeAddition = virtualStudent.currentYearFees[termKey] || 0;
    }

    const newBalance = virtualStudent.balance + feeAddition;

    const event: SimulationEvent = {
        id: Date.now().toString(),
        date: new Date().toISOString().split('T')[0],
        type: eventType,
        description: `${description} - Fees Applied`,
        amount: feeAddition, // Representing new debt added
        balanceAfter: newBalance
    };

    if (eventType === 'GRADUATION') {
         toast({ title: "Graduated!", description: "Student has completed all classes.", className: "bg-blue-100 border-blue-200" });
    } else if (eventType === 'CLASS_UPGRADE') {
        toast({ title: "Promoted", description: `Advanced to ${nextClass}` });
    } else {
        toast({ title: "New Term", description: `Term ${nextTerm} fees added.` });
    }

    setVirtualStudent({
        ...virtualStudent,
        term: nextTerm,
        className: nextClass,
        classUid: nextClassUid,
        year: nextYear,
        balance: newBalance,
        currentYearFees: newYearFees,
        history: [event, ...virtualStudent.history]
    });
  };

  const resetSimulation = () => {
      setVirtualStudent(null);
      setPaymentAmount("");
      setSimulationResult(null);
  };


  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-slate-50 dark:bg-slate-900">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full min-w-0">
          <Header />
           <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-slate-50 dark:bg-slate-900 relative">
            <div className="p-6 space-y-6 max-w-7xl mx-auto">
              
              {/* Header Area */}
              <div className="flex justify-between items-center">
                  <div className="flex flex-col gap-2">
                    <h1 className="text-3xl font-bold flex items-center gap-2">
                      <Calculator className="h-8 w-8 text-blue-600" />
                      Enrollment Fee Simulator
                    </h1>
                    <p className="text-gray-500">
                      {virtualStudent 
                        ? "Active Simulation Session" 
                        : "Simulate student enrollment to verify active terms and fee calculations."}
                    </p>
                  </div>
                  {virtualStudent && (
                      <Button variant="outline" onClick={resetSimulation} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          Exit Simulation
                      </Button>
                  )}
              </div>

              {!virtualStudent ? (
                // --- SETUP VIEW ---
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="border-l-4 border-l-blue-500 shadow-md">
                    <CardHeader>
                        <CardTitle>Step 1: Configure Student</CardTitle>
                        <CardDescription>Set up the initial enrollment parameters.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                        <Label>Admission Date</Label>
                        <div className="relative">
                            <CalendarIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                            <Input 
                            type="date" 
                            value={admissionDate}
                            onChange={(e) => setAdmissionDate(e.target.value)}
                            className="pl-9"
                            />
                        </div>
                        </div>

                        <div className="space-y-2">
                        <Label>Class to Enroll</Label>
                        <Select value={selectedClass} onValueChange={setSelectedClass}>
                            <SelectTrigger>
                            <SelectValue placeholder="Select Class" />
                            </SelectTrigger>
                            <SelectContent>
                            {schoolClasses.map(cls => (
                                <SelectItem key={cls.classUid} value={cls.name}>{cls.name}</SelectItem>
                            ))}
                            </SelectContent>
                        </Select>
                        </div>

                        {schoolType?.includes("Boarding") && (
                            <div className="space-y-2">
                                <Label>Student Type</Label>
                                <Select value={studentType} onValueChange={setStudentType}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Boarding">Boarding</SelectItem>
                                        <SelectItem value="Day School">Day School</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="flex items-center space-x-2 border p-3 rounded-md bg-amber-50 border-amber-200">
                             <input 
                                type="checkbox" 
                                id="ongoingSwitch" 
                                className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                checked={isOngoing}
                                onChange={(e) => setIsOngoing(e.target.checked)}
                             />
                             <Label htmlFor="ongoingSwitch" className="flex-1 cursor-pointer text-amber-900 font-medium">
                                 Simulate Ongoing Student?
                             </Label>
                        </div>
                        
                        {isOngoing && (
                            <div className="space-y-4 animate-in fade-in slide-in-from-top-2 border-l-2 border-amber-300 pl-3">
                                <div className="space-y-2">
                                    <Label className="text-amber-900">Balance Type</Label>
                                    <div className="flex gap-4">
                                        <div className="flex items-center space-x-2">
                                            <input 
                                                type="radio" 
                                                id="bal-debit" 
                                                name="balanceType" 
                                                value="debit"
                                                checked={balanceType === "debit"}
                                                onChange={() => setBalanceType("debit")}
                                                className="text-amber-600 focus:ring-amber-500"
                                            />
                                            <Label htmlFor="bal-debit" className="font-normal cursor-pointer">Debit (Owes School)</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <input 
                                                type="radio" 
                                                id="bal-credit" 
                                                name="balanceType" 
                                                value="credit"
                                                checked={balanceType === "credit"}
                                                onChange={() => setBalanceType("credit")}
                                                className="text-green-600 focus:ring-green-500"
                                            />
                                            <Label htmlFor="bal-credit" className="font-normal cursor-pointer">Credit (Overpaid)</Label>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-amber-900">
                                        {balanceType === 'debit' ? "Outstanding Debt Amount" : "Credit Amount"}
                                    </Label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2.5 text-gray-500">KES</span>
                                        <Input 
                                            type="number" 
                                            value={initialDebt}
                                            onChange={(e) => setInitialDebt(e.target.value)}
                                            className="pl-12 border-amber-300 focus:border-amber-500 focus:ring-amber-500"
                                            placeholder="e.g. 5000"
                                        />
                                    </div>
                                    <p className="text-xs text-amber-700">
                                        {balanceType === 'debit' 
                                            ? "Amount the student still owes from previous terms." 
                                            : "Amount the student has paid in advance (surplus)."
                                        }
                                    </p>
                                </div>
                            </div>
                        )}

                        <Button 
                            className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                            disabled={!simulationResult}
                            onClick={handleEnroll}
                        >
                            Start Simulation
                        </Button>
                    </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-green-500 shadow-md bg-gray-50/50">
                    <CardHeader>
                        <CardTitle>Preview Results</CardTitle>
                        <CardDescription>Predicted fees based on selection.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {simulationResult ? (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-white rounded border">
                                    <span className="text-gray-600">Detected Term:</span>
                                    <Badge variant="outline">{detectedTerm?.title || "N/A"}</Badge>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                                    {['term1', 'term2', 'term3'].map(t => (
                                        <div key={t} className={`p-2 rounded ${simulationResult.applicable[t] === 0 ? 'bg-gray-100 text-gray-400 line-through' : 'bg-blue-50 text-blue-700 font-medium'}`}>
                                            <div className="text-xs uppercase break-all">{t.replace('term', 'Term ')}</div>
                                            {formatMoney(simulationResult.potential[t])}
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg border border-green-200">
                                    <div className="flex flex-col text-left">
                                        <span className="text-sm font-semibold text-green-700">Annual Fees Overview</span>
                                        <span className="text-xs text-green-600 font-normal">Total payable for the year</span>
                                    </div>
                                    <span className="text-xl font-bold text-green-700">{formatMoney(simulationResult.applicable.annual)}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-10 text-gray-400">
                                <Info className="h-10 w-10 mx-auto mb-2 opacity-30" />
                                <p>Select inputs to preview</p>
                            </div>
                        )}
                    </CardContent>
                    </Card>
                </div>
              ) : (
                // --- ACTIVE SIMULATION VIEW ---
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    
                    {/* LEFT COLUMN: STATUS & CONTROLS */}
                    <div className="space-y-6 lg:col-span-1">
                        {/* Status Card */}
                        <Card className="bg-white border-l-4 border-l-red-500 shadow-lg">
                            <CardContent className="pt-6">
                                <div className="mb-4">
                                    <h3 className="text-sm font-medium text-gray-500 uppercase">Total Outstanding Debt</h3>
                                    <div className="text-4xl font-bold text-red-600 mt-1">
                                        {formatMoney(virtualStudent.balance)}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Amount owed to school</p>
                                </div>
                                <Separator className="my-4" />
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="block text-gray-500 text-xs">Class</span>
                                        <span className="font-semibold">{virtualStudent.className}</span>
                                    </div>
                                    <div>
                                        <span className="block text-gray-500 text-xs">Current Term</span>
                                        <span className="font-semibold">Term {virtualStudent.term}</span>
                                    </div>
                                    <div>
                                        <span className="block text-gray-500 text-xs">Year of Study</span>
                                        <span className="font-semibold">Year {virtualStudent.year}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Actions Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Actions</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Payment */}
                                <div className="space-y-3">
                                    <Label>Record Payment</Label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <span className="absolute left-3 top-2.5 text-gray-500">KES</span>
                                            <Input 
                                                type="number" 
                                                className="pl-12" 
                                                placeholder="Amount"
                                                value={paymentAmount}
                                                onChange={e => setPaymentAmount(e.target.value)}
                                            />
                                        </div>
                                        <Button onClick={handlePayment} size="icon" className="bg-green-600 hover:bg-green-700">
                                            <CheckCircle2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-400">Reduces the outstanding debt.</p>
                                </div>

                                <Separator />

                                {/* Progression */}
                                <div className="space-y-3">
                                    <Label>Time Travel</Label>
                                    <Button onClick={handleNextTerm} className="w-full flex justify-between items-center" variant="outline">
                                        <span>
                                            {virtualStudent.term === 3 ? "Complete Year & Promote" : "Proceed to Next Term"}
                                        </span>
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                    <p className="text-xs text-gray-400">
                                        {virtualStudent.term === 3 
                                            ? "Moves to next class. Adds new Term 1 fees." 
                                            : "Moves to next term. Adds term fees to debt."}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* RIGHT COLUMN: HISTORY & LEDGER */}
                    <div className="space-y-6 lg:col-span-2">
                        
                        {/* Fee Breakdown Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <School className="h-4 w-4" />
                                    Annual Fee Structure ({virtualStudent.className})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-3 gap-4">
                                    {['term1', 'term2', 'term3'].map((term, idx) => (
                                        <div key={term} className={`p-3 rounded-lg border ${virtualStudent.term === idx + 1 ? 'bg-red-50 border-red-200 ring-1 ring-red-300' : 'bg-gray-50 border-gray-100'}`}>
                                            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Term {idx + 1} Fees</div>
                                            <div className="font-medium text-gray-900">
                                                {formatMoney((virtualStudent.currentYearFees as any)[term] || 0)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Ledger History */}
                        <Card className="flex flex-col h-[500px]">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <History className="h-5 w-5" />
                                    Statement of Account
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="flex-1 p-0 overflow-hidden">
                                <ScrollArea className="h-full px-6 pb-6">
                                    <div className="space-y-4">
                                        {virtualStudent.history.length === 0 && (
                                            <div className="text-center text-gray-400 py-8">No events yet.</div>
                                        )}
                                        {virtualStudent.history.map((evt, idx) => (
                                            <div key={evt.id} className="flex gap-4 items-start relative pb-6 last:pb-0">
                                                {idx !== virtualStudent.history.length - 1 && (
                                                    <div className="absolute left-[19px] top-8 bottom-0 w-0.5 bg-gray-200" />
                                                )}
                                                
                                                <div className={`
                                                    relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2
                                                    ${evt.type === 'PAYMENT' ? 'bg-green-100 border-green-200 text-green-600' : 
                                                      evt.type === 'TERM_SWITCH' || evt.type === 'ENROLLMENT' ? 'bg-red-50 border-red-200 text-red-600' :
                                                      evt.type === 'CLASS_UPGRADE' ? 'bg-purple-100 border-purple-200 text-purple-600' :
                                                      evt.type === 'GRADUATION' ? 'bg-amber-100 border-amber-200 text-amber-600' :
                                                      'bg-gray-100 border-gray-200 text-gray-600'}
                                                `}>
                                                    {evt.type === 'PAYMENT' ? <CreditCard className="h-5 w-5" /> : 
                                                     evt.type === 'CLASS_UPGRADE' ? <TrendingUp className="h-5 w-5" /> :
                                                     evt.type === 'GRADUATION' ? <GraduationCap className="h-5 w-5" /> :
                                                     <ArrowRight className="h-5 w-5" />}
                                                </div>

                                                <div className="flex-1 bg-white p-3 rounded-lg border shadow-sm">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <div className="font-semibold text-gray-900">{evt.description}</div>
                                                            <div className="text-xs text-gray-500">{new Date(evt.date).toDateString()}</div>
                                                        </div>
                                                        <div className="text-right">
                                                            {evt.amount > 0 && (
                                                                <div className={`font-medium ${evt.type === 'PAYMENT' ? 'text-green-600' : 'text-red-600'}`}>
                                                                    {evt.type === 'PAYMENT' ? 'Paid: ' : 'New Debt: '}
                                                                    {formatMoney(evt.amount)}
                                                                </div>
                                                            )}
                                                            <div className="text-xs font-bold text-gray-500 mt-1">
                                                                Debt: {formatMoney(evt.balanceAfter)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                            </CardContent>
                        </Card>
                    </div>
                </div>
              )}

            </div>
           </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default TestCaseSimulation;
